<template>
  <section class="buy-plan-page">
    <div class="buy-plan-hero">
      <div class="container">
        <router-link :to="{ path: '/plans', hash: 'planes-2027' }" class="buy-back">
          <i class="fas fa-arrow-left mr-2"></i>
          Volver a planes
        </router-link>
        <h1 class="buy-hero-title">Activa tu plan</h1>
        <p class="buy-hero-sub">Completa tus datos y paga de forma segura. El acceso llega a tu correo después del pago.</p>
      </div>
    </div>

    <div class="container buy-plan-body">
      <b-spinner v-if="loadingPage" class="d-block mx-auto my-5" label="Cargando..." />

      <b-alert
        v-else-if="pageError"
        show
        variant="warning"
        class="buy-alert"
      >
        {{ pageError }}
        <div class="mt-3">
          <router-link :to="{ path: '/plans', hash: 'planes-2027' }" class="btn btn-buy-secondary">
            Ver planes
          </router-link>
        </div>
      </b-alert>

      <div v-else class="buy-layout">
        <aside class="buy-plan-summary">
          <span v-if="plan.catalogBadge" class="buy-badge">{{ plan.catalogBadge }}</span>
          <h2 class="buy-plan-name">{{ plan.name }}</h2>
          <div class="buy-plan-price">
            <span class="buy-amount">${{ displayPrice }}</span>
            <small>{{ priceSuffix }}</small>
          </div>
          <div v-if="offerPrices.length" class="buy-intervals" role="group" aria-label="Forma de pago">
            <button
              v-for="price in offerPrices"
              :key="price.billing_interval"
              type="button"
              :aria-pressed="billingInterval === price.billing_interval"
              @click="billingInterval = price.billing_interval"
            >
              {{ intervalName(price.billing_interval) }} · ${{ quotedAmount(price) }}
            </button>
          </div>
          <p v-if="offersPrepaidWellness" class="buy-interval-note">
            Pagando trimestral o anual, tus exámenes del Wellness están disponibles desde el día 1.
          </p>
          <p v-if="plan.catalogFor" class="buy-plan-for">{{ plan.catalogFor }}</p>
          <ul v-if="summaryBenefits.length" class="buy-benefits">
            <li v-for="(b, i) in summaryBenefits" :key="i">
              <b>{{ b[0] }}</b>
              <span v-if="b[1]">{{ b[1] }}</span>
            </li>
          </ul>
          <p class="buy-secure-note">
            <i class="fas fa-lock mr-2"></i>
            Pago seguro con Stripe. No guardamos tu tarjeta en este sitio.
          </p>
        </aside>

        <div class="buy-form-card">
          <h3 class="buy-form-title">Tus datos</h3>
          <p class="buy-form-sub">Después del pago te enviamos el acceso por correo.</p>

          <b-alert v-if="formError" show dismissible variant="danger" class="mb-3" @dismissed="formError = null">
            {{ formError }}
          </b-alert>

          <form @submit.prevent="submitCheckout">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="buy-name" class="form-label">Nombre <span class="text-danger">*</span></label>
                <input
                  id="buy-name"
                  v-model.trim="form.name"
                  type="text"
                  class="form-control buy-input"
                  autocomplete="given-name"
                  required
                />
              </div>
              <div class="col-md-6 mb-3">
                <label for="buy-lastname" class="form-label">Apellido <span class="text-danger">*</span></label>
                <input
                  id="buy-lastname"
                  v-model.trim="form.lastname"
                  type="text"
                  class="form-control buy-input"
                  autocomplete="family-name"
                  required
                />
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="buy-phone" class="form-label">Teléfono <span class="text-danger">*</span></label>
                <input
                  id="buy-phone"
                  v-model.trim="form.phone"
                  type="tel"
                  class="form-control buy-input"
                  autocomplete="tel"
                  required
                />
              </div>
              <div class="col-md-6 mb-3">
                <label for="buy-email" class="form-label">Email <span class="text-danger">*</span></label>
                <input
                  id="buy-email"
                  v-model.trim="form.email"
                  type="email"
                  class="form-control buy-input"
                  autocomplete="email"
                  required
                />
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="buy-dob" class="form-label">
                  Fecha de nacimiento
                  <span v-if="requiresDateBirth" class="text-danger">*</span>
                </label>
                <div class="buy-dob-wrap">
                  <flat-pickr
                    id="buy-dob"
                    v-model="form.date_birth"
                    :config="dobPickerConfig"
                    class="form-control buy-input buy-dob-input"
                    placeholder="Elige tu fecha"
                    autocomplete="bday"
                  />
                  <i class="fas fa-calendar-alt buy-dob-icon" aria-hidden="true"></i>
                </div>
                <small v-if="isFamily" class="form-text text-muted">
                  Obligatoria. El titular debe tener {{ adultMinAge }} años o más.
                </small>
                <small v-else-if="requiresDateBirth" class="form-text text-muted">
                  Obligatorio para Tu Salud Senior (60 años o más).
                </small>
                <small v-else class="form-text text-muted">
                  Opcional. Usa el calendario para elegir el día.
                </small>
              </div>
              <div class="col-md-6 mb-3">
                <label for="buy-agent" class="form-label">¿Te refirió un agente?</label>
                <select id="buy-agent" v-model="form.agent_id" class="form-control buy-input">
                  <option value="">No aplica</option>
                  <option v-for="agent in agents" :key="agent.id" :value="String(agent.id)">
                    {{ agent.name }}
                  </option>
                </select>
              </div>
            </div>

            <template v-if="isFamily">
              <h3 class="buy-form-title h5 mt-2">Integrantes</h3>
              <p class="buy-form-sub">
                El titular cuenta. Mínimo 2 personas y máximo {{ familyMaxPeople }}. Los niños de {{ ageLimits.childMinAge }} a {{ adultMinAge - 1 }} años no llevan teléfono ni correo.
              </p>
              <div v-for="(member, index) in members" :key="'member-' + index" class="border rounded p-3 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <strong>Integrante {{ index + 1 }}</strong>
                  <button v-if="members.length > 1" type="button" class="btn btn-sm btn-link text-danger" @click="removeMember(index)">
                    Quitar
                  </button>
                </div>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Nombre</label>
                    <input v-model.trim="member.name" type="text" class="form-control buy-input" required />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Apellido</label>
                    <input v-model.trim="member.lastname" type="text" class="form-control buy-input" required />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Fecha de nacimiento</label>
                    <input v-model="member.date_birth" type="text" class="form-control buy-input" placeholder="MM-DD-YYYY" required />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Parentesco</label>
                    <select v-model="member.relationship" class="form-control buy-input">
                      <option v-for="option in relationships" :key="option.value" :value="option.value">{{ option.text }}</option>
                    </select>
                  </div>
                  <template v-if="memberShowsContact(member)">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Teléfono</label>
                      <input v-model.trim="member.phone" type="tel" class="form-control buy-input" />
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Correo</label>
                      <input v-model.trim="member.email" type="email" class="form-control buy-input" />
                    </div>
                  </template>
                </div>
                <small class="text-muted">{{ memberHint(member) }}</small>
              </div>
              <button v-if="canAddMember" type="button" class="btn btn-buy-ghost mb-4" @click="addMember">Agregar integrante</button>
              <p v-else class="buy-form-sub mb-4">Llegaste al máximo de {{ familyMaxPeople }} personas, contando al titular.</p>
            </template>

            <template v-if="isCompany">
              <h3 class="buy-form-title h5 mt-2">Empresa</h3>
              <p class="buy-form-sub">Quien compra administra la empresa y no ocupa un cupo. Mínimo 5 cupos.</p>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label" for="buy-company">Nombre de la empresa <span class="text-danger">*</span></label>
                  <input id="buy-company" v-model.trim="company.name" type="text" class="form-control buy-input" required />
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label" for="buy-ein">EIN</label>
                  <input id="buy-ein" v-model.trim="company.ein" type="text" class="form-control buy-input" placeholder="Opcional" />
                </div>
                <div class="col-md-8 mb-3">
                  <label class="form-label" for="buy-address">Dirección</label>
                  <input id="buy-address" v-model.trim="company.address" type="text" class="form-control buy-input" placeholder="Opcional" />
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label" for="buy-seats">Cupos <span class="text-danger">*</span></label>
                  <input id="buy-seats" v-model.number="company.seats" type="number" min="5" step="1" class="form-control buy-input" required />
                </div>
              </div>
            </template>

            <div class="form-check mb-4">
              <input id="buy-consent" v-model="form.consent" type="checkbox" class="form-check-input" required />
              <label class="form-check-label" for="buy-consent">
                Acepto las políticas y consiento recibir comunicaciones de
                <strong>Hispano Medical Plan</strong>. Puedo cancelar cuando quiera.
              </label>
            </div>

            <div class="buy-actions">
              <router-link :to="{ path: '/plans', hash: 'planes-2027' }" class="btn btn-buy-ghost">
                Cancelar
              </router-link>
              <button type="submit" class="btn btn-buy-cta" :disabled="submitting">
                <span v-if="submitting">
                  <i class="fas fa-spinner fa-spin mr-2"></i>
                  Redirigiendo al pago…
                </span>
                <span v-else>
                  Continuar al pago
                  <i class="fas fa-chevron-right ml-2"></i>
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { BSpinner, BAlert } from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { Spanish } from 'flatpickr/dist/l10n/es.js'
import plansService from '@/core/services/api/landings/plans'
import { enrichApiPlan } from '../data/hmpPlans2027'
import {
  formatRegisterApiMessage,
  isSeniorPlan,
  isValidDateBirthMdy,
  maskDateBirthInput
} from '../utils/registerUserPlanHelpers'
import { intervalCopy, familyTierAmount, pickBillingPrice, planBillingPrices, planKindOf } from '@/core/utils/membershipBilling'
import {
  ageFromDateBirth,
  emptyFamilyMember,
  FAMILY_MAX_PEOPLE,
  familyAgeLimits,
  familyMemberCountFromPeople,
  familyMemberPayload,
  memberAudience,
  RELATIONSHIP_OPTIONS,
  validateFamilyMember,
} from '@/core/utils/membershipFamily'

/**
 * Vista pública de compra: datos + agente → Stripe Checkout.
 * Ruta: /comprar-plan/:planId (id de memberships_parameters).
 */
export default {
  name: 'BuyPlan',
  components: { BSpinner, BAlert, flatPickr },
  data() {
    return {
      loadingPage: true,
      pageError: null,
      formError: null,
      submitting: false,
      plan: null,
      billingInterval: 'month',
      agents: [],
      members: [emptyFamilyMember()],
      relationships: RELATIONSHIP_OPTIONS,
      familyMaxPeople: FAMILY_MAX_PEOPLE,
      company: {
        name: '',
        ein: '',
        address: '',
        seats: 5
      },
      form: {
        name: '',
        lastname: '',
        phone: '',
        email: '',
        date_birth: '',
        agent_id: '',
        consent: false
      }
    }
  },
  computed: {
    planId() {
      return Number(this.$route.params.planId) || 0
    },
    requiresDateBirth() {
      return isSeniorPlan(this.plan) || this.isFamily
    },
    isFamily() {
      return planKindOf(this.plan) === 'family'
    },
    isCompany() {
      return planKindOf(this.plan) === 'company'
    },
    ageLimits() {
      return familyAgeLimits(this.plan)
    },
    adultMinAge() {
      return this.ageLimits.adultMinAge
    },
    familyHeadcount() {
      return 1 + this.members.length
    },
    canAddMember() {
      return this.familyHeadcount < FAMILY_MAX_PEOPLE
    },
    offerPrices() {
      if (!this.plan) return []
      return planBillingPrices(this.plan, this.plan.price)
    },
    selectedOffer() {
      return pickBillingPrice(this.offerPrices, this.billingInterval)
    },
    displayPrice() {
      if (this.selectedOffer) return this.quotedAmount(this.selectedOffer)
      if (!this.plan) return '—'
      return this.plan.price != null ? this.plan.price : '—'
    },
    priceSuffix() {
      return intervalCopy(this.selectedOffer && this.selectedOffer.billing_interval).suffix
    },
    offersPrepaidWellness() {
      return this.offerPrices.some((price) => price.billing_interval === 'quarter' || price.billing_interval === 'year')
    },
    summaryBenefits() {
      const list = (this.plan && this.plan.marketingBenefits) || []
      return list.slice(0, 5)
    },
    /** Calendario DOB: mes/año en dropdown; valor API MM-DD-YYYY */
    dobPickerConfig() {
      const maxDate = new Date()
      if (isSeniorPlan(this.plan)) {
        maxDate.setFullYear(maxDate.getFullYear() - 60)
      } else if (this.isFamily) {
        maxDate.setFullYear(maxDate.getFullYear() - this.adultMinAge)
      }
      return {
        locale: Spanish,
        dateFormat: 'm-d-Y',
        altInput: true,
        altFormat: 'j \\d\\e F \\d\\e Y',
        allowInput: false,
        disableMobile: true,
        static: true,
        monthSelectorType: 'dropdown',
        minDate: new Date(1900, 0, 1),
        maxDate,
        defaultDate: this.form.date_birth || null
      }
    }
  },
  watch: {
    '$route.params.planId': {
      immediate: false,
      handler() {
        this.bootstrap()
      }
    }
  },
  mounted() {
    this.applyPrefillFromQuery()
    this.bootstrap()
  },
  methods: {
    applyPrefillFromQuery() {
      const q = this.$route.query || {}
      if (q.name) this.form.name = String(q.name)
      if (q.lastname) this.form.lastname = String(q.lastname)
      if (q.phone) this.form.phone = String(q.phone)
      if (q.email) this.form.email = String(q.email)
      if (q.date_birth) this.form.date_birth = maskDateBirthInput(String(q.date_birth))
      if (q.billing_interval) this.billingInterval = String(q.billing_interval)
      if (q.people != null) {
        this.members = Array.from({ length: familyMemberCountFromPeople(q.people) }, () => emptyFamilyMember())
      }
    },
    quotedAmount(price) {
      if (!price) return 0
      if (this.isFamily) return familyTierAmount(price, this.familyHeadcount)
      if (this.isCompany) return Number(price.amount) * (Number(this.company.seats) || 0)
      return price.amount
    },
    addMember() {
      if (!this.canAddMember) return
      this.members.push(emptyFamilyMember())
    },
    removeMember(index) {
      if (this.members.length <= 1) return
      this.members.splice(index, 1)
    },
    memberShowsContact(member) {
      const audience = memberAudience(ageFromDateBirth(member.date_birth), this.ageLimits)
      return audience !== 'child' && audience !== 'underage'
    },
    memberHint(member) {
      const audience = memberAudience(ageFromDateBirth(member.date_birth), this.ageLimits)
      if (audience === 'child') return 'Niño: no recibe acceso.'
      if (audience === 'underage') return `Menor de ${this.ageLimits.childMinAge} años: el plan no lo acepta.`
      if (audience === 'adult') return 'Adulto. El correo es opcional; si lo escribes, recibe su acceso.'
      return 'Fecha en formato MM-DD-YYYY.'
    },
    intervalName(interval) {
      return intervalCopy(interval).name
    },
    keepBillingInterval() {
      const prices = this.offerPrices
      if (!prices.some((price) => price.billing_interval === this.billingInterval)) {
        this.billingInterval = prices.length ? prices[0].billing_interval : 'month'
      }
    },
    async bootstrap() {
      this.loadingPage = true
      this.pageError = null
      this.formError = null
      try {
        const [plansRes, agentsRes] = await Promise.all([
          plansService.getAllPlans(),
          plansService.getMemberPlanAgents().catch(() => ({ data: [] }))
        ])

        let plansData = []
        if (plansRes && plansRes.status === 'ok' && Array.isArray(plansRes.data)) {
          plansData = plansRes.data
        } else if (Array.isArray(plansRes)) {
          plansData = plansRes
        } else if (plansRes && Array.isArray(plansRes.data)) {
          plansData = plansRes.data
        }

        const raw = plansData.find((p) => Number(p.id) === this.planId)
        if (!raw) {
          this.pageError = 'No encontramos ese plan. Elige otro desde la lista de planes.'
          this.plan = null
          return
        }

        const enriched = enrichApiPlan(raw)
        this.plan = enriched
        this.keepBillingInterval()
        const agentsRaw = (agentsRes && agentsRes.data) || []
        this.agents = Array.isArray(agentsRaw) ? agentsRaw : []
      } catch (err) {
        console.error('Error al cargar compra de plan:', err)
        this.pageError = 'No pudimos cargar el plan. Intenta de nuevo en unos minutos.'
      } finally {
        this.loadingPage = false
      }
    },
    validateForm() {
      if (!this.form.name || !this.form.lastname || !this.form.phone || !this.form.email) {
        return 'Completa nombre, apellido, teléfono y email.'
      }
      if (!this.form.consent) {
        return 'Debes aceptar las políticas para continuar.'
      }
      if (this.requiresDateBirth && !this.form.date_birth) {
        return 'La fecha de nacimiento es obligatoria para este plan'
      }
      if (this.form.date_birth && !isValidDateBirthMdy(this.form.date_birth)) {
        return 'La fecha de nacimiento debe tener el formato MM-DD-YYYY'
      }
      if (this.isFamily) {
        const holderAge = ageFromDateBirth(this.form.date_birth)
        if (holderAge != null && holderAge < this.adultMinAge) return `El titular debe tener ${this.adultMinAge} años o más.`
        if (this.familyHeadcount < 2) return 'El plan Familiar necesita al menos 2 personas.'
        if (this.familyHeadcount > FAMILY_MAX_PEOPLE) return `El plan Familiar permite máximo ${FAMILY_MAX_PEOPLE} personas, contando al titular.`
        for (let index = 0; index < this.members.length; index += 1) {
          const message = validateFamilyMember(this.members[index], `Miembro ${index + 2}`, this.ageLimits)
          if (message) return message
        }
      }
      if (this.isCompany) {
        if (!this.company.name) return 'Escribe el nombre de la empresa.'
        const seats = Number(this.company.seats)
        if (!Number.isInteger(seats) || seats < 5) return 'Los cupos son un número entero, mínimo 5.'
      }
      return null
    },
    async submitCheckout() {
      if (this.submitting || !this.plan) return
      this.formError = null
      const localError = this.validateForm()
      if (localError) {
        this.formError = localError
        return
      }

      this.submitting = true
      try {
        const payload = {
          name: this.form.name,
          lastname: this.form.lastname,
          phone: this.form.phone,
          email: this.form.email,
          memberships_parameters_id: Number(this.plan.id),
          billing_interval: (this.selectedOffer && this.selectedOffer.billing_interval) || 'month'
        }
        if (this.form.date_birth) {
          payload.date_birth = this.form.date_birth.trim()
        }
        if (this.isFamily) {
          payload.members = this.members.map((member) => familyMemberPayload(member, this.ageLimits))
        }
        if (this.isCompany) {
          payload.seats = Number(this.company.seats)
          payload.company = { name: this.company.name }
          if (this.company.ein) payload.company.ein = this.company.ein
          if (this.company.address) payload.company.address = this.company.address
        }
        payload.agent_id = this.form.agent_id
          ? Number(this.form.agent_id)
          : null

        const res = await plansService.createMemberPlanCheckout(payload)
        const checkoutUrl = res && res.data && res.data.checkout_url
        if (!checkoutUrl) {
          this.formError = 'No se pudo iniciar el pago. Intenta de nuevo.'
          return
        }
        window.location.href = checkoutUrl
      } catch (err) {
        console.error('Error al iniciar checkout:', err)
        this.formError = formatRegisterApiMessage(
          err,
          'No se pudo iniciar el pago. Intenta de nuevo.'
        )
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.buy-plan-page {
  min-height: 70vh;
  background: $landing-bg-gray;
  padding-bottom: 4rem;
}

.buy-plan-hero {
  background: $landing-primary-color;
  color: #fff;
  padding: 2.25rem 0 2.5rem;
}

.buy-back {
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
  margin-bottom: 1rem;
  text-decoration: none;

  &:hover {
    color: $landing-secondary-color;
    text-decoration: none;
  }
}

.buy-hero-title {
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 800;
  margin: 0 0 0.5rem;
  color: #fff;
}

.buy-hero-sub {
  margin: 0;
  max-width: 36rem;
  color: rgba(255, 255, 255, 0.82);
  font-size: 1.05rem;
}

.buy-plan-body {
  margin-top: -1.5rem;
}

.buy-alert {
  margin-top: 2rem;
}

.buy-layout {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
  align-items: start;

  @media (min-width: 992px) {
    grid-template-columns: minmax(260px, 340px) 1fr;
  }
}

.buy-plan-summary {
  background: $landing-primary-color;
  color: #fff;
  border-radius: 12px;
  padding: 1.75rem 1.5rem;
  box-shadow: 0 12px 28px $landing-primary-rgba-18;
}

.buy-badge {
  display: inline-block;
  background: $landing-secondary-color;
  color: $landing-primary-color;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.25rem 0.6rem;
  border-radius: 4px;
  margin-bottom: 0.75rem;
}

.buy-plan-name {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 0 0.5rem;
}

.buy-intervals {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 0;

  button {
    text-align: left;
    border: 1px solid rgba(255, 255, 255, 0.45);
    background: transparent;
    color: #fff;
    border-radius: 10px;
    padding: 8px 12px;
    font-weight: 600;

    &[aria-pressed='true'] {
      background: #fff;
      color: $landing-primary-color;
    }
  }
}

.buy-interval-note {
  font-size: 0.9rem;
  opacity: 0.9;
}

.buy-plan-price {
  margin-bottom: 0.75rem;

  .buy-amount {
    font-size: 2.25rem;
    font-weight: 800;
    line-height: 1;
  }

  small {
    margin-left: 0.25rem;
    opacity: 0.85;
  }
}

.buy-plan-for {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1rem;
}

.buy-benefits {
  list-style: none;
  padding: 0;
  margin: 0 0 1.25rem;

  li {
    display: flex;
    flex-direction: column;
    padding: 0.55rem 0;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    font-size: 0.9rem;

    b {
      font-weight: 700;
    }

    span {
      color: rgba(255, 255, 255, 0.72);
      font-size: 0.85rem;
    }
  }
}

.buy-secure-note {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
}

.buy-form-card {
  background: $landing-bg-white;
  border-radius: 12px;
  border: 1px solid $landing-border-light;
  padding: 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 8px 24px $landing-primary-rgba-08;
}

.buy-form-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: $landing-primary-color;
  margin: 0 0 0.35rem;
}

.buy-form-sub {
  color: $landing-text-medium;
  margin-bottom: 1.25rem;
}

.form-label {
  font-weight: 600;
  color: $landing-text-dark;
  font-size: 0.9rem;
}

.buy-input {
  border-color: $landing-border-light;
  border-radius: 8px;
  min-height: 44px;

  &:focus {
    border-color: $landing-teal;
    box-shadow: 0 0 0 0.15rem $landing-secondary-rgba-30;
  }
}

.buy-dob-wrap {
  position: relative;

  .buy-dob-icon {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: $landing-teal;
    pointer-events: none;
    z-index: 2;
  }

  ::v-deep .flatpickr-input,
  ::v-deep .form-control,
  ::v-deep input.buy-dob-input,
  ::v-deep input.input {
    border-color: $landing-border-light;
    border-radius: 8px;
    min-height: 44px;
    width: 100%;
    padding-right: 2.5rem;
    background: #fff;
    cursor: pointer;
    color: $landing-text-dark;

    &:focus {
      border-color: $landing-teal;
      box-shadow: 0 0 0 0.15rem $landing-secondary-rgba-30;
      outline: none;
    }
  }

  ::v-deep .flatpickr-input[readonly],
  ::v-deep .input[readonly] {
    background: #fff;
  }

  ::v-deep .flatpickr-calendar {
    margin-top: 6px;
    border-radius: 12px;
    border: 1px solid $landing-border-light;
    box-shadow: 0 12px 28px $landing-primary-rgba-15;
    font-family: inherit;
  }

  ::v-deep .flatpickr-months {
    .flatpickr-month {
      color: $landing-primary-color;
    }

    .flatpickr-prev-month,
    .flatpickr-next-month {
      fill: $landing-primary-color;
      color: $landing-primary-color;

      &:hover svg {
        fill: $landing-teal;
      }
    }
  }

  ::v-deep select.flatpickr-monthDropdown-months {
    color: $landing-primary-color;
    font-weight: 600;
  }

  ::v-deep .numInputWrapper {
    input.cur-year {
      color: $landing-primary-color;
      font-weight: 600;
    }
  }

  ::v-deep .flatpickr-weekdays,
  ::v-deep span.flatpickr-weekday {
    color: $landing-text-medium;
  }

  ::v-deep .flatpickr-day {
    color: $landing-text-dark;
    border-radius: 8px;

    &:hover {
      background: $landing-bg-gray;
      border-color: $landing-bg-gray;
    }

    &.selected,
    &.selected:hover {
      background: $landing-secondary-color;
      border-color: $landing-secondary-color;
      color: $landing-primary-color;
      font-weight: 700;
    }

    &.today {
      border-color: $landing-teal;
    }

    &.flatpickr-disabled,
    &.prevMonthDay,
    &.nextMonthDay {
      color: #b9cad6;
    }
  }
}


.buy-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;
  align-items: center;
  padding-top: 0.5rem;
}

.btn-buy-cta {
  background: $landing-secondary-color;
  border: none;
  color: $landing-primary-color;
  font-weight: 700;
  padding: 0.7rem 1.35rem;
  border-radius: 8px;

  &:hover:not(:disabled),
  &:focus {
    background: $landing-blue-light;
    color: $landing-primary-color;
  }

  &:disabled {
    opacity: 0.7;
  }

  i {
    color: inherit;
  }
}

.btn-buy-ghost,
.btn-buy-secondary {
  border: 1px solid $landing-border-light;
  color: $landing-primary-color;
  background: #fff;
  font-weight: 600;
  padding: 0.65rem 1.1rem;
  border-radius: 8px;
  text-decoration: none;

  &:hover {
    background: $landing-bg-gray;
    color: $landing-primary-color;
    text-decoration: none;
  }
}
</style>
